import { CSSProperties } from 'react';
import { isEqual } from 'radash';
import { VIDEO_ASPECT_RATIO } from '../constants/player';
import { VideoCropPosition } from '../types/RemotionPlayerConfigTypes/Scene';
import { roundToNDecimalPlaces } from './mathUtils';

const TOP_FOCUS_PADDING = 0.2;
const BOTTOM_FOCUS_PADDING = 0;
const LEFT_RIGHT_FOCUS_PADDING = 0;

// This will force it to include atleast 50% screen width and height contains the face
export const getFaceBoundCoordinateWithPaddingAndRelativeScale = ({
  coordinates,
  parentContainerWidth,
  parentContainerHeight,
  videoWidth,
  videoHeight
}: {
  coordinates: VideoCropPosition;
  parentContainerWidth: number;
  parentContainerHeight: number;
  videoWidth: number;
  videoHeight: number;
}) => {
  const newCoordinates = {
    top_left: {
      x: Math.max(0, coordinates.top_left.x - (coordinates.bottom_right.x - coordinates.top_left.x) * 0.5),
      y: Math.max(0, coordinates.top_left.y - (coordinates.bottom_right.y - coordinates.top_left.y) * 0.5)
    },
    bottom_right: {
      x: Math.min(1, coordinates.bottom_right.x + (coordinates.bottom_right.x - coordinates.top_left.x) * 0.5),
      y: Math.min(1, coordinates.bottom_right.y + (coordinates.bottom_right.y - coordinates.top_left.y) * 0.5)
    }
  };
  return convertToParentContainerAspectRatio({
    coordinates: newCoordinates,
    parentContainerWidth,
    parentContainerHeight,
    videoWidth,
    videoHeight
  });
};

export const convertToParentContainerAspectRatio = ({
  coordinates,
  parentContainerWidth,
  parentContainerHeight,
  videoWidth,
  videoHeight
}: {
  coordinates: VideoCropPosition;
  parentContainerWidth: number;
  parentContainerHeight: number;
  videoWidth: number;
  videoHeight: number;
}) => {
  const newFocusCoordinates = {
    top_left: { x: coordinates.top_left.x, y: coordinates.top_left.y },
    bottom_right: { x: coordinates.bottom_right.x, y: coordinates.bottom_right.y }
  };
  newFocusCoordinates.top_left.x =
    (newFocusCoordinates.top_left.x * videoWidth + (parentContainerWidth - videoWidth) / 2) / parentContainerWidth;
  newFocusCoordinates.top_left.y =
    (newFocusCoordinates.top_left.y * videoHeight + (parentContainerHeight - videoHeight) / 2) / parentContainerHeight;
  newFocusCoordinates.bottom_right.x =
    (newFocusCoordinates.bottom_right.x * videoWidth + (parentContainerWidth - videoWidth) / 2) / parentContainerWidth;
  newFocusCoordinates.bottom_right.y =
    (newFocusCoordinates.bottom_right.y * videoHeight + (parentContainerHeight - videoHeight) / 2) /
    parentContainerHeight;

  newFocusCoordinates.top_left.x = roundToNDecimalPlaces(newFocusCoordinates.top_left.x, 4);
  newFocusCoordinates.top_left.y = roundToNDecimalPlaces(newFocusCoordinates.top_left.y, 4);
  newFocusCoordinates.bottom_right.x = roundToNDecimalPlaces(newFocusCoordinates.bottom_right.x, 4);
  newFocusCoordinates.bottom_right.y = roundToNDecimalPlaces(newFocusCoordinates.bottom_right.y, 4);

  return newFocusCoordinates;
};

export const getPersonBoundCoordinateWithPaddingAndRelativeScale = ({
  coordinates,
  parentContainerWidth,
  parentContainerHeight,
  videoWidth,
  videoHeight
}: {
  coordinates: VideoCropPosition;
  parentContainerWidth: number;
  parentContainerHeight: number;
  videoWidth: number;
  videoHeight: number;
}) => {
  const newFocusCoordinates = {
    top_left: {
      x: Math.max(
        0,
        coordinates.top_left.x - (coordinates.bottom_right.x - coordinates.top_left.x) * LEFT_RIGHT_FOCUS_PADDING
      ),
      y: Math.max(0, coordinates.top_left.y - (coordinates.bottom_right.y - coordinates.top_left.y) * TOP_FOCUS_PADDING)
    },
    bottom_right: {
      x: Math.min(
        1,
        coordinates.bottom_right.x + (coordinates.bottom_right.x - coordinates.top_left.x) * LEFT_RIGHT_FOCUS_PADDING
      ),
      y: Math.min(
        1,
        coordinates.bottom_right.y + (coordinates.bottom_right.y - coordinates.top_left.y) * BOTTOM_FOCUS_PADDING
      )
    }
  };
  return convertToParentContainerAspectRatio({
    coordinates: newFocusCoordinates,
    parentContainerWidth,
    parentContainerHeight,
    videoWidth,
    videoHeight
  });
};

export const getCropStyles = (
  cropInformation: VideoCropPosition | null,
  newContainerStyle: CSSProperties,
  playerAspectRatio: number,
  // Pass if need to avoid overlapping with other crops
  allOtherCropInformation?: Record<string, VideoCropPosition>,
  isManualRepositioned?: boolean,
  videoAspectRatio?: number,
  faceCoordinates?: VideoCropPosition | null
): CSSProperties => {
  videoAspectRatio = videoAspectRatio ?? VIDEO_ASPECT_RATIO;
  if (!cropInformation || !newContainerStyle?.width || !newContainerStyle?.height) {
    return {};
  }
  // This is a fixed aspect ratio for all our videos

  const parentContainerWidth = Number(newContainerStyle.width.toString().replace('%', '').replace('px', ''));
  const parentContainerHeight =
    Number(newContainerStyle.height.toString().replace('%', '').replace('px', '')) / playerAspectRatio;

  const parentContainerAspectRatio = parentContainerWidth / parentContainerHeight;
  const isCompleteVideoFitByWidth = videoAspectRatio >= parentContainerAspectRatio;
  const videoWidth = isCompleteVideoFitByWidth ? parentContainerWidth : parentContainerHeight * videoAspectRatio;

  const videoHeight = videoWidth / videoAspectRatio;

  const isCropInformationFaceCoordinates = isEqual(cropInformation, faceCoordinates);

  // With object-fit contain we will have background color on either top/bottom or left/right of the video
  // Hence all the calculation needs to be done to prevent extending view beyond view. Happens due to different aspect ratios of crop and parent container
  // This is also used to prevent overlapping with other crops in case of upload videos
  let topLeftXMaxBoundary =
    videoAspectRatio > parentContainerAspectRatio
      ? 0
      : roundToNDecimalPlaces((parentContainerWidth - videoWidth) / 2 / parentContainerWidth, 4);
  let topLeftYMaxBoundary =
    videoAspectRatio > parentContainerAspectRatio
      ? roundToNDecimalPlaces((parentContainerHeight - videoHeight) / parentContainerHeight / 2, 4)
      : 0;
  let bottomRightXMaxBoundary = roundToNDecimalPlaces(1 - topLeftXMaxBoundary, 4);
  let bottomRightYMaxBoundary = roundToNDecimalPlaces(1 - topLeftYMaxBoundary, 4);

  const cropInformationWithRelativeScale = convertToParentContainerAspectRatio({
    coordinates: cropInformation,
    parentContainerWidth,
    parentContainerHeight,
    videoWidth,
    videoHeight
  });

  let topLeftX = cropInformationWithRelativeScale.top_left.x;
  let topLeftY = cropInformationWithRelativeScale.top_left.y;
  let bottomRightX = cropInformationWithRelativeScale.bottom_right.x;
  let bottomRightY = cropInformationWithRelativeScale.bottom_right.y;

  let mustIncludeBoundary: VideoCropPosition | undefined = undefined;

  if (!isManualRepositioned) {
    if (isCropInformationFaceCoordinates) {
      mustIncludeBoundary = getFaceBoundCoordinateWithPaddingAndRelativeScale({
        coordinates: cropInformation,
        parentContainerWidth,
        parentContainerHeight,
        videoWidth,
        videoHeight
      });
    } else {
      mustIncludeBoundary = getPersonBoundCoordinateWithPaddingAndRelativeScale({
        // can be converted to face later
        coordinates: cropInformation,
        parentContainerWidth,
        parentContainerHeight,
        videoWidth,
        videoHeight
      });
    }
    topLeftX = mustIncludeBoundary.top_left.x;
    topLeftY = mustIncludeBoundary.top_left.y;
    bottomRightX = mustIncludeBoundary.bottom_right.x;
    bottomRightY = mustIncludeBoundary.bottom_right.y;
  }

  if (allOtherCropInformation) {
    const xBoundaries: number[] = [];
    const yBoundaries: number[] = [];

    const convertedCropInformation = convertToParentContainerAspectRatio({
      coordinates: cropInformation,
      parentContainerWidth,
      parentContainerHeight,
      videoWidth,
      videoHeight
    });

    Object.entries(allOtherCropInformation).forEach(([_, value]) => {
      const convertedValues = convertToParentContainerAspectRatio({
        coordinates: value,
        parentContainerWidth,
        parentContainerHeight,
        videoWidth,
        videoHeight
      });
      const isVerticallyOverlapping =
        (convertedValues.bottom_right.y > convertedCropInformation.top_left.y &&
          convertedValues.bottom_right.y < convertedCropInformation.bottom_right.y) ||
        (convertedValues.top_left.y > convertedCropInformation.top_left.y &&
          convertedValues.top_left.y < convertedCropInformation.bottom_right.y);

      const isHorizontallyOverlapping =
        (convertedValues.bottom_right.x > convertedCropInformation.top_left.x &&
          convertedValues.bottom_right.x < convertedCropInformation.bottom_right.x) ||
        (convertedValues.top_left.x > convertedCropInformation.top_left.x &&
          convertedValues.top_left.x < convertedCropInformation.bottom_right.x);

      if (isVerticallyOverlapping && isHorizontallyOverlapping) {
        return;
      }
      if (isVerticallyOverlapping) {
        xBoundaries.push(convertedValues.top_left.x, convertedValues.bottom_right.x);
      } else if (isHorizontallyOverlapping) {
        yBoundaries.push(convertedValues.top_left.y, convertedValues.bottom_right.y);
      } else {
        if (parentContainerAspectRatio > 1) {
          xBoundaries.push(convertedValues.top_left.x, convertedValues.bottom_right.x);
        } else {
          yBoundaries.push(convertedValues.top_left.y, convertedValues.bottom_right.y);
        }
      }
    });

    const possibleLeftBoundaries = xBoundaries.filter(boundary => boundary < convertedCropInformation.top_left.x);
    const possibleRightBoundaries = xBoundaries.filter(boundary => boundary > convertedCropInformation.bottom_right.x);
    const possibleTopBoundaries = yBoundaries.filter(boundary => boundary < convertedCropInformation.top_left.y);
    const possibleBottomBoundaries = yBoundaries.filter(boundary => boundary > convertedCropInformation.bottom_right.y);

    const nearestLeftBoundary = possibleLeftBoundaries?.length ? Math.max(...possibleLeftBoundaries) : undefined;
    const nearestRightBoundary = possibleRightBoundaries?.length ? Math.min(...possibleRightBoundaries) : undefined;
    const nearestTopBoundary = possibleTopBoundaries?.length ? Math.max(...possibleTopBoundaries) : undefined;
    const nearestBottomBoundary = possibleBottomBoundaries?.length ? Math.min(...possibleBottomBoundaries) : undefined;

    if (nearestLeftBoundary) {
      topLeftXMaxBoundary = Math.max(nearestLeftBoundary, topLeftXMaxBoundary);
    }
    if (nearestRightBoundary) {
      bottomRightXMaxBoundary = Math.min(nearestRightBoundary, bottomRightXMaxBoundary);
    }
    if (nearestTopBoundary) {
      topLeftYMaxBoundary = Math.max(nearestTopBoundary, topLeftYMaxBoundary);
    }
    if (nearestBottomBoundary) {
      bottomRightYMaxBoundary = Math.min(nearestBottomBoundary, bottomRightYMaxBoundary);
    }
  }

  let centerXWrtContainer = (topLeftX + bottomRightX) / 2;
  let centerYWrtContainer = (topLeftY + bottomRightY) / 2;

  topLeftX = roundToNDecimalPlaces(topLeftX, 4);
  topLeftY = roundToNDecimalPlaces(topLeftY, 4);
  bottomRightX = roundToNDecimalPlaces(bottomRightX, 4);
  bottomRightY = roundToNDecimalPlaces(bottomRightY, 4);

  const cropAspectRatio = ((bottomRightX - topLeftX) / (bottomRightY - topLeftY)) * parentContainerAspectRatio;

  // --------------- Done to prevent extending view beyond view. Happens due to different aspect ratios of crop and parent container-------------- //
  // This adjustment could fail in case if cropped selection it too big and cannot be adjusted to hide black background
  if (roundToNDecimalPlaces(cropAspectRatio, 4) !== roundToNDecimalPlaces(parentContainerAspectRatio, 4)) {
    // Expansion of the box
    if (roundToNDecimalPlaces(cropAspectRatio, 4) > roundToNDecimalPlaces(parentContainerAspectRatio, 4)) {
      topLeftY = centerYWrtContainer - (bottomRightX - topLeftX) / 2;
      bottomRightY = centerYWrtContainer + (bottomRightX - topLeftX) / 2;
    } else {
      topLeftX = centerXWrtContainer - (bottomRightY - topLeftY) / 2;
      bottomRightX = centerXWrtContainer + (bottomRightY - topLeftY) / 2;
    }

    // siding of the box
    if (topLeftY < topLeftYMaxBoundary && bottomRightY + (topLeftYMaxBoundary - topLeftY) <= bottomRightYMaxBoundary) {
      bottomRightY += topLeftYMaxBoundary - topLeftY;
      topLeftY = topLeftYMaxBoundary;
    } else if (
      bottomRightY > bottomRightYMaxBoundary &&
      topLeftY - (bottomRightY - bottomRightYMaxBoundary) >= topLeftYMaxBoundary
    ) {
      topLeftY += bottomRightYMaxBoundary - bottomRightY;
      bottomRightY = bottomRightYMaxBoundary;
    }

    if (topLeftX < topLeftXMaxBoundary && bottomRightX + (topLeftXMaxBoundary - topLeftX) <= bottomRightXMaxBoundary) {
      bottomRightX += topLeftXMaxBoundary - topLeftX;
      topLeftX = topLeftXMaxBoundary;
    } else if (
      bottomRightX > bottomRightXMaxBoundary &&
      topLeftX + (bottomRightXMaxBoundary - bottomRightX) >= topLeftXMaxBoundary
    ) {
      topLeftX += bottomRightXMaxBoundary - bottomRightX;
      bottomRightX = bottomRightXMaxBoundary;
    }

    centerXWrtContainer = (topLeftX + bottomRightX) / 2;
    centerYWrtContainer = (topLeftY + bottomRightY) / 2;
  }

  topLeftX = roundToNDecimalPlaces(topLeftX, 4);
  topLeftY = roundToNDecimalPlaces(topLeftY, 4);
  bottomRightX = roundToNDecimalPlaces(bottomRightX, 4);
  bottomRightY = roundToNDecimalPlaces(bottomRightY, 4);
  centerXWrtContainer = roundToNDecimalPlaces(centerXWrtContainer, 4);
  centerYWrtContainer = roundToNDecimalPlaces(centerYWrtContainer, 4);

  // ------------------ adjustment end ---------------

  let scale = 1 / (bottomRightX - topLeftX);

  // --------------- If above adjustment fails then need to increase the scale --------
  const hasBlackBackgroundOnTopBottom = topLeftY < topLeftYMaxBoundary || bottomRightY > bottomRightYMaxBoundary;
  const hasBlackBackgroundOnLeftRight = bottomRightX > bottomRightXMaxBoundary || topLeftX < topLeftXMaxBoundary;
  if (hasBlackBackgroundOnLeftRight || hasBlackBackgroundOnTopBottom) {
    if (hasBlackBackgroundOnTopBottom) {
      const newCropTop = Math.max(topLeftY, topLeftYMaxBoundary);
      const newCropBottom = Math.min(bottomRightY, bottomRightYMaxBoundary);

      centerYWrtContainer = (newCropTop + newCropBottom) / 2;
      scale = 1 / (newCropBottom - newCropTop);
    }
    if (hasBlackBackgroundOnLeftRight) {
      const newCropLeft = Math.max(topLeftX, topLeftXMaxBoundary);
      const newCropRight = Math.min(bottomRightX, bottomRightXMaxBoundary);

      const maxHeightPossible = newCropRight - newCropLeft;

      let newCropTop = centerYWrtContainer - maxHeightPossible / 2;
      let newCropBottom = centerYWrtContainer + maxHeightPossible / 2;

      // Is Cutting the Head off the video
      const isTopMustIncludeNotSatisfied = mustIncludeBoundary && mustIncludeBoundary.top_left.y < newCropTop;

      if (mustIncludeBoundary && isTopMustIncludeNotSatisfied) {
        newCropBottom = newCropBottom - (newCropTop - mustIncludeBoundary.top_left.y);
        newCropTop = mustIncludeBoundary.top_left.y;
      }

      centerXWrtContainer = (newCropLeft + newCropRight) / 2;
      centerYWrtContainer = (newCropTop + newCropBottom) / 2;
      scale = 1 / (newCropRight - newCropLeft);
    }
  }

  // ------------------ scale adjustment end ---------------

  return {
    position: 'absolute',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transform: `translate(${-1 * (centerXWrtContainer - 0.5) * scale * 100}%, ${-1 * (centerYWrtContainer - 0.5) * scale * 100}%) scale(${scale})`
  };
};
/**
 * FIT strategy for crop styles — ensures the entire crop region is visible without any content cut off.
 */
export const getFitCropStyles = (
  cropInformation: VideoCropPosition | null,
  newContainerStyle: CSSProperties,
  playerAspectRatio: number,
  videoAspectRatio?: number
): CSSProperties => {
  videoAspectRatio = videoAspectRatio ?? VIDEO_ASPECT_RATIO;
  if (!cropInformation || !newContainerStyle.width || !newContainerStyle.height) {
    return {};
  }

  const parentContainerWidth = Number(newContainerStyle.width.toString().replace('%', '').replace('px', ''));
  const parentContainerHeight =
    Number(newContainerStyle.height.toString().replace('%', '').replace('px', '')) / playerAspectRatio;

  const parentContainerAspectRatio = parentContainerWidth / parentContainerHeight;
  const isCompleteVideoFitByWidth = videoAspectRatio >= parentContainerAspectRatio;
  const videoWidth = isCompleteVideoFitByWidth ? parentContainerWidth : parentContainerHeight * videoAspectRatio;
  const videoHeight = videoWidth / videoAspectRatio;

  const converted = convertToParentContainerAspectRatio({
    coordinates: cropInformation,
    parentContainerWidth,
    parentContainerHeight,
    videoWidth,
    videoHeight
  });

  const cropWidth = roundToNDecimalPlaces(converted.bottom_right.x - converted.top_left.x, 4);
  const cropHeight = roundToNDecimalPlaces(converted.bottom_right.y - converted.top_left.y, 4);

  let centerXWrtContainer = roundToNDecimalPlaces((converted.top_left.x + converted.bottom_right.x) / 2, 4);
  let centerYWrtContainer = roundToNDecimalPlaces((converted.top_left.y + converted.bottom_right.y) / 2, 4);

  // FIT scale: choose the smaller of the two possible scales so both dimensions fit within the container.
  // No clamping — FIT intentionally accepts letterbox/pillarbox bars.
  const scale = Math.min(1 / cropWidth, 1 / cropHeight);

  // Clip the element to only the crop region (in pre-transform element coords).
  // clip-path is applied before the CSS transform, so the clipped areas become
  // transparent — the parent's background color shows through as FIT bars.
  const clipTop = roundToNDecimalPlaces(converted.top_left.y * 100, 4);
  const clipRight = roundToNDecimalPlaces((1 - converted.bottom_right.x) * 100, 4);
  const clipBottom = roundToNDecimalPlaces((1 - converted.bottom_right.y) * 100, 4);
  const clipLeft = roundToNDecimalPlaces(converted.top_left.x * 100, 4);

  centerXWrtContainer = roundToNDecimalPlaces(centerXWrtContainer, 4);
  centerYWrtContainer = roundToNDecimalPlaces(centerYWrtContainer, 4);

  return {
    position: 'absolute',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    clipPath: `inset(${clipTop}% ${clipRight}% ${clipBottom}% ${clipLeft}%)`,
    transform: `translate(${-1 * (centerXWrtContainer - 0.5) * scale * 100}%, ${-1 * (centerYWrtContainer - 0.5) * scale * 100}%) scale(${scale})`
  };
};
